import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useAppToast } from "@/components/AppToast";
import type { CreateCalculationFormData } from "../CreateCalculationPanel.constants";
import useAiDraftController from "../../../hooks/useAiDraftController";
import type { ComposerFile } from "./ChatComposer.component";
import {
  buildMessages,
  type SubmittedTurn,
  type CompletedTurn,
  type ScopeField,
} from "./chatMessages";
import type { AiChatProps } from "./AiChat.component";

export interface UseAiChatOptions {
  /** The calculation header the user set in the wizard, as labelled rows, shown
   *  as the opening scope card so the thread reads as an already-started
   *  conversation. */
  scopeFields: ScopeField[];
  /** Fired once when a draft reaches READY, with the new calculation id. The
   *  panel uses it to close + navigate (mirrors the manual Create flow). */
  onSuccess: (calculationId: string) => void;
}

/**
 * The chat surface's sole backend boundary. Wraps `useAiDraftController` (upload
 * + note + job polling + phase machine) and adapts it into the presentational
 * `AiChat` props:
 *
 *  - snapshots the note + admitted file names when the user hits send, so the
 *    user's bubble stays stable across polls/retries;
 *  - accumulates each completed non-success turn (noData / failed) into a
 *    client-side history so the thread reads as a continuing conversation — the
 *    user refines in the same panel and earlier attempts stay visible;
 *  - maps scope + history + current phase into the ordered `ChatMessage[]`;
 *  - on success (READY) fires the app success toast and closes the panel, so
 *    there's no dangling result and no duplicate-calculation follow-up.
 *
 * When server-side streaming / multi-turn arrives, this hook is the only thing
 * that changes; the components render from `messages` regardless.
 */
const useAiChat = (
  form: UseFormReturn<CreateCalculationFormData>,
  { scopeFields, onSuccess }: UseAiChatOptions,
): AiChatProps => {
  const { addToast } = useAppToast();
  const {
    phase,
    files,
    note,
    setNote,
    addFiles,
    removeFile,
    canSubmit,
    submit,
    retry,
  } = useAiDraftController(form);

  // The turn captured at send time — stable so the user bubble doesn't mutate
  // while the next turn is composed. Null before the first submit.
  const [submitted, setSubmitted] = useState<SubmittedTurn | null>(null);
  // Completed non-success turns, oldest first. Success is never archived — it
  // closes the panel.
  const [history, setHistory] = useState<CompletedTurn[]>([]);
  const nextTurnIdRef = useRef(0);

  // Blocks a double-send in the async gap between the click and phase→running
  // (`busy` only covers an already-running job). Cleared on every phase change,
  // by which point start()/retry() has resolved.
  const submittingRef = useRef(false);
  useEffect(() => {
    submittingRef.current = false;
  }, [phase]);

  // Success is terminal: toast + close, once per READY job. Guard by jobId so a
  // late extra poll after READY can't fire the toast (or navigate) twice.
  const successFiredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (phase.kind !== "ready") return;
    const { calculationId, jobId } = phase.job;
    if (!calculationId || successFiredForRef.current === jobId) return;
    successFiredForRef.current = jobId;
    const planningYear = form.getValues().planningYear;
    addToast({
      appearance: "success",
      message: "Budget created successfully",
      description: `Budget ${planningYear} has been created with your uploaded data.`,
    });
    onSuccess(calculationId);
  }, [phase, addToast, onSuccess, form]);

  const busy = phase.kind === "running";

  const composerFiles = useMemo<ComposerFile[]>(
    () =>
      files.map((f) => ({
        id: f.id,
        name: f.file.name,
        status: f.status,
        rejectionReason: f.rejectionReason,
      })),
    [files],
  );

  const messages = useMemo(
    () => buildMessages(phase, submitted, { scopeFields, history }),
    [phase, submitted, scopeFields, history],
  );

  const onSend = useCallback(() => {
    if (!canSubmit || busy || submittingRef.current) return;
    submittingRef.current = true;

    // Starting a new turn on top of a completed non-success result → archive the
    // finished turn so it stays in the thread above the new one.
    if ((phase.kind === "noData" || phase.kind === "failed") && submitted) {
      const archived: CompletedTurn = {
        id: nextTurnIdRef.current++,
        submitted,
        phase,
      };
      setHistory((prev) => [...prev, archived]);
    }

    setSubmitted({
      text: note.trim(),
      files: files
        .filter((f) => f.status === "admitted")
        .map((f) => ({ id: f.id, name: f.file.name })),
    });
    submit();
  }, [canSubmit, busy, phase, submitted, note, files, submit]);

  return {
    messages,
    note,
    files: composerFiles,
    busy,
    canSend: canSubmit,
    onNoteChange: setNote,
    onFilesPicked: (picked: File[]) => {
      // addFiles caps at maxFiles and returns a message when the cap is hit; surface it
      // rather than dropping the extra files silently.
      addFiles(picked)
        .then((error) => {
          if (error) addToast({ appearance: "warning", message: error });
        })
        .catch(() => {});
    },
    onRemoveFile: removeFile,
    onSend,
    onRetry: () => void retry(),
  };
};

export default useAiChat;
